import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtMoney, fmtNum } from "@/lib/api";
import { Alert, PageHeader, Panel, Spinner, Tag } from "@/components/common";

const pct = (v) => (v == null ? "—" : `${v}%`);
const COLS = [["Spend", "cost", fmtMoney], ["Impr.", "impressions", fmtNum], ["Clicks", "clicks", fmtNum], ["CTR", "ctr", pct], ["Avg CPC", "cpc", fmtMoney],
  ["Search IS", "search_is", pct], ["Form", "form", fmtNum], ["WhatsApp", "whatsapp", fmtNum], ["Call", "call", fmtNum], ["Google conv.", "google_conversions", fmtNum],
  ["Cost / lead", "cost_per_lead", fmtMoney], ["Booked", "booked", fmtNum], ["Cost / booking", "cost_per_booking", fmtMoney]];

const Row = ({ name, x, total, testid }) => (
  <tr className={`border-t border-[#1e293b] ${total ? "font-bold text-slate-50" : "text-slate-300"}`} data-testid={testid}>
    <td className="px-2 py-2">{name}</td>{COLS.map(([h, k, f]) => <td key={h} className="px-2 py-2 text-right font-mono">{f(x[k])}</td>)}
  </tr>
);

const Terms = ({ c }) => (
  <div className="text-xs" data-testid="report-terms">
    <div className="mb-1 font-semibold text-slate-200">{c.campaign.replace("TRL | ", "")}</div>
    <div className="text-slate-400">Top: {c.top_terms.map((t) => `${t.term} (${t.clicks})`).join(", ") || "—"}</div>
    <div className="text-red-300">Wasted: {c.wasted_terms.map((t) => `${t.term} (${fmtMoney(t.cost)})`).join(", ") || "none"}</div>
  </div>
);

const Commentary = ({ c }) => (
  <Panel title="Commentary · Claude Sonnet 5.5" icon={Sparkles} testid="report-commentary">
    {c?.error ? <div className="text-sm text-slate-400">{c.error}</div> : <div className="space-y-3 text-sm text-slate-300">
      <p>{c?.summary}</p>
      <div><b className="text-emerald-300">Working</b><ul className="list-disc pl-5">{c?.working?.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
      <div><b className="text-amber-300">Issues</b><ul className="list-disc pl-5">{c?.issues?.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
      <div><b className="text-cyan-300">Recommendations</b><ol className="list-decimal pl-5" data-testid="report-recommendations">{c?.recommendations?.map((x, i) => <li key={i}><b>{x.title}</b>: {x.detail}</li>)}</ol></div>
      <p className="text-xs text-slate-500">Suggestions only: nothing changes in Google Ads unless you approve a proposal in Approvals.</p>
    </div>}
  </Panel>
);

export default function LaunchReport() {
  const [start, setStart] = useState("2026-11-04");
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback((refresh = false) => {
    setBusy("load"); setErr("");
    api.get("/launch-report", { params: { start: start === "2026-11-04" ? undefined : start, refresh } }).then((x) => setR(x.data)).catch((e) => setErr(errMsg(e))).finally(() => setBusy(""));
  }, [start]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    setBusy("send");
    try { await api.post("/launch-report/send", { start: start === "2026-11-04" ? null : start }); toast.success("Launch report emailed"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div data-testid="launch-report-page">
      <PageHeader eyebrow="Dashboard · Launch" title="Launch report" description="Seven-day comparison of the 3 TRL campaigns. Default window starts at launch (3 Nov 2026, 16:19 UTC). Sent automatically on 11 Nov at 9:00am SGT."
        actions={<div className="flex items-center gap-2">
          <input data-testid="report-start-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="cc-input w-40 px-2 py-1.5 text-sm" />
          <button data-testid="report-refresh-btn" onClick={() => load(true)} disabled={!!busy} className="cc-btn cc-btn-ghost whitespace-nowrap px-3 py-1.5 text-xs"><RefreshCw className="h-3.5 w-3.5" /> Recalculate</button>
          <button data-testid="report-send-btn" onClick={send} disabled={!!busy || !r} className="cc-btn cc-btn-primary whitespace-nowrap px-3 py-1.5 text-xs">{busy === "send" ? <Spinner className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />} Send now</button>
        </div>} />
      {err && <Alert testid="report-error">{err}</Alert>}
      {busy === "load" && !r && <Spinner />}
      {r && <div className="space-y-6">
        <div className="flex flex-wrap gap-2 text-xs"><Tag tone="cyan" testid="report-window">{r.dates[0]} → {r.dates[1]}</Tag><Tag tone={r.source === "google" ? "emerald" : "amber"}>{r.source === "google" ? "live window query" : "daily sync"}</Tag><Tag>calculated {fmtDate(r.computed_at)}</Tag>{r.sent_at && <Tag tone="emerald">emailed {fmtDate(r.sent_at)}</Tag>}</div>
        {r.source_note && <Alert tone="amber">{r.source_note}</Alert>}
        <div className="cc-card overflow-x-auto p-4"><table className="w-full text-xs" data-testid="report-table">
          <thead className="text-slate-500"><tr><th className="px-2 py-1.5 text-left">Campaign</th>{COLS.map(([h]) => <th key={h} className="px-2 py-1.5 text-right font-medium">{h}</th>)}</tr></thead>
          <tbody>{r.campaigns.map((c, i) => <Row key={c.campaign} name={c.campaign.replace("TRL | ", "")} x={c} testid={`report-row-${i}`} />)}<Row name="Total" x={r.totals} total testid="report-row-total" /></tbody>
        </table></div>
        <Alert tone="muted" testid="report-baseline">{r.baseline.note} New campaigns this window: {fmtMoney(r.totals.cost_per_lead)} per lead, {fmtMoney(r.totals.cost_per_booking)} per booking.</Alert>
        <Panel title="Search terms" testid="report-terms-panel"><div className="grid gap-4 md:grid-cols-3">{r.campaigns.map((c) => <Terms key={c.campaign} c={c} />)}</div></Panel>
        <Commentary c={r.commentary} />
      </div>}
    </div>
  );
}
